// src/version.ts
var LIB_VERSION = "0.6.4";

// src/unmapped-code-tracker.ts
var TRACKER = /* @__PURE__ */ new Map();
function recordUnmappedCode(lib, code) {
  const key2 = `${lib}::${code}`;
  const prev = TRACKER.get(key2);
  if (prev) {
    prev.hitCount += 1;
    return;
  }
  TRACKER.set(key2, { lib, code, hitCount: 1 });
}
function getUnmappedCodes() {
  return TRACKER;
}
function resetUnmappedCodes() {
  TRACKER.clear();
}

// src/mode.ts
var currentMode = null;
var modeLocked = false;
var SINGLETON_SYMBOL = /* @__PURE__ */ Symbol.for("norkron.errors.singleton");
(function registerInstance() {
  const g = globalThis;
  const existing = g[SINGLETON_SYMBOL];
  if (existing) {
    existing.instances += 1;
  } else {
    g[SINGLETON_SYMBOL] = { instances: 1 };
  }
})();
var VALID_MODES = /* @__PURE__ */ new Set(["production", "development", "test"]);
function isValidMode(value) {
  return typeof value === "string" && VALID_MODES.has(value);
}
function readEnvMode() {
  const proc = globalThis.process;
  if (!proc || typeof proc !== "object") return null;
  const env = proc.env;
  if (!env || typeof env !== "object") return null;
  const raw = env.NORKRON_ERRORS_MODE;
  return isValidMode(raw) ? raw : null;
}
function resolveInitialMode() {
  const fromEnv = readEnvMode();
  return fromEnv ?? "production";
}
function setNorkronErrorsMode(mode) {
  if (!isValidMode(mode)) {
    throw new Error(
      `[norkron/errors] invalid mode: expected exactly one of 'production' | 'development' | 'test'; received ${describe(mode)}`
    );
  }
  if (modeLocked) {
    throw new Error(
      `[norkron/errors] mode is locked; called setNorkronErrorsMode(${JSON.stringify(
        mode
      )}) after first error/warning`
    );
  }
  currentMode = mode;
}
function getNorkronErrorsMode() {
  if (currentMode === null) {
    currentMode = resolveInitialMode();
  }
  return currentMode;
}
function __lockMode() {
  if (currentMode === null) {
    currentMode = resolveInitialMode();
  }
  modeLocked = true;
}
function __resetNorkronErrorsMode() {
  const proc = globalThis.process;
  const envValue = proc?.env?.NORKRON_ERRORS_MODE;
  if (envValue !== "test") {
    throw new Error(
      `[norkron/errors] __resetNorkronErrorsMode is test-only; requires process.env.NORKRON_ERRORS_MODE === 'test' (got ${describe(envValue)})`
    );
  }
  currentMode = null;
  modeLocked = false;
}
function __captureModeSnapshot() {
  const mode = getNorkronErrorsMode();
  const snapshot = Object.freeze({
    mode,
    capturedAt: Date.now()
  });
  return snapshot;
}
function describe(value) {
  if (value === null) return "null";
  if (value === void 0) return "undefined";
  if (typeof value === "string") return JSON.stringify(value);
  return `${typeof value}(${String(value)})`;
}

// src/pii.ts
var RE_CREDIT_CARD = /(?:\d[ -]?){12,19}/;
var RE_SSN = /\b\d{3}-\d{2}-\d{4}\b/;
var RE_EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
var RE_IBAN = /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/;
var RE_IPV4 = /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\b/;
var RE_IPV6 = /\b(?:[0-9A-Fa-f]{1,4}:){2,7}[0-9A-Fa-f]{1,4}\b|::1\b|\b::\b/;
var RE_BEARER = /Bearer\s+[A-Za-z0-9._\-+/=]{8,}/;
var RE_AWS_AKID = /\bAKIA[0-9A-Z]{16}\b/;
var RE_PEM = /-----BEGIN [A-Z ]*PRIVATE KEY-----/;
var RE_GENERIC_KEY = /\b(?:api[_-]?key|apikey|secret|token|password|pwd|auth)\s*[:=]\s*['"]?[A-Za-z0-9._\-+/=]{16,}['"]?/i;
var RE_JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/;
var RE_GITHUB_PAT = /\bgh[pousr]_[A-Za-z0-9]{30,}\b/;
var RE_PHONE_E164 = /(?<![\w.])\+[1-9]\d{7,14}(?![\w.])/;
var RE_PHONE_NATIONAL = /(?<![\w.])\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}(?![\w.])/;
function luhnValid(candidate) {
  const digits = candidate.replace(/[^\d]/g, "");
  if (digits.length < 12 || digits.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    const ch = digits.charCodeAt(i) - 48;
    if (ch < 0 || ch > 9) return false;
    let n = ch;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}
var PII_PATTERNS = [
  { kind: "private-key", re: RE_PEM },
  { kind: "jwt", re: RE_JWT },
  { kind: "github-pat", re: RE_GITHUB_PAT },
  { kind: "aws-key", re: RE_AWS_AKID },
  { kind: "bearer-token", re: RE_BEARER },
  { kind: "api-key", re: RE_GENERIC_KEY },
  { kind: "credit-card", re: RE_CREDIT_CARD, validate: luhnValid },
  { kind: "ssn", re: RE_SSN },
  { kind: "iban", re: RE_IBAN },
  { kind: "email", re: RE_EMAIL },
  { kind: "ip", re: RE_IPV4 },
  { kind: "ip", re: RE_IPV6 },
  { kind: "phone", re: RE_PHONE_E164 },
  { kind: "phone", re: RE_PHONE_NATIONAL }
];
function firstPiiKind(s) {
  if (typeof s !== "string") return void 0;
  if (s.length === 0) return void 0;
  const scan = s.length > 4096 ? s.slice(0, 4096) : s;
  for (const p of PII_PATTERNS) {
    const m = scan.match(p.re);
    if (m && (p.validate === void 0 || p.validate(m[0]))) {
      return p.kind;
    }
  }
  return void 0;
}
function detectPii(s) {
  return firstPiiKind(s) !== void 0;
}

// src/inspect.ts
var INSPECT_CUSTOM = /* @__PURE__ */ Symbol.for("nodejs.util.inspect.custom");
var TO_PRIMITIVE = Symbol.toPrimitive;
function applyInspectHook(obj, prodShape) {
  Object.defineProperty(obj, INSPECT_CUSTOM, {
    value: function inspectHook(_depth, _opts, _inspect) {
      return prodShape();
    },
    enumerable: false,
    writable: false,
    configurable: false
  });
}
function applyToPrimitiveHook(obj, asString) {
  Object.defineProperty(obj, TO_PRIMITIVE, {
    value: function toPrimitiveHook(hint) {
      if (hint === "number") return Number.NaN;
      return asString();
    },
    enumerable: false,
    writable: false,
    configurable: false
  });
}

// src/redaction.ts
var DEFAULT_MAX_DEPTH = 10;
var DEFAULT_MAX_KEYS = 100;
var DEFAULT_MAX_NODES = 1e3;
var DEFAULT_MAX_STACK_FRAMES_DEV = 64;
var SAMPLE_LEN = 20;
var STRING_TRUNCATE = 100;
var PII_REDACTED = "[redacted: pii-detected]";
var PROD_REDACTED = "[redacted]";
var PROD_STACK = "[norkron-stack:redacted]";
var CIRCULAR = "[Circular]";
function redactString(s, mode) {
  if (mode === "production") return PROD_REDACTED;
  if (typeof s !== "string") return PROD_REDACTED;
  if (detectPii(s)) return PII_REDACTED;
  if (s.length <= STRING_TRUNCATE) return s;
  return s.slice(0, STRING_TRUNCATE) + "\u2026";
}
function redactReceived(value, mode, opts) {
  if (value === void 0 && mode !== "production") {
    return { kind: "undefined" };
  }
  if (mode === "production") {
    return prodReceivedShape(value);
  }
  const budget = {
    nodes: 0,
    maxDepth: opts?.maxDepth ?? DEFAULT_MAX_DEPTH,
    maxKeys: opts?.maxKeys ?? DEFAULT_MAX_KEYS,
    maxNodes: opts?.maxNodes ?? DEFAULT_MAX_NODES
  };
  return walkReceived(value, 0, /* @__PURE__ */ new WeakSet(), budget);
}
function prodReceivedShape(value) {
  if (value === null) return { kind: "null" };
  if (value === void 0) return { kind: "undefined" };
  if (Array.isArray(value)) return { kind: "array", type: "array", length: 0 };
  const t = typeof value;
  switch (t) {
    case "string":
    case "number":
    case "boolean":
    case "symbol":
    case "bigint":
      return { kind: "primitive", type: t };
    case "function":
      return { kind: "function", type: "function" };
    case "object":
      return { kind: "object", type: "object" };
    /* istanbul ignore next */
    default:
      return { kind: "object", type: "object" };
  }
}
function walkReceived(value, depth, seen, budget) {
  budget.nodes++;
  if (value === null) return { kind: "null" };
  if (value === void 0) return { kind: "undefined" };
  const t = typeof value;
  if (t === "string") {
    const s = value;
    const sample2 = sampleString(s);
    return { kind: "primitive", type: "string", sample: sample2, length: s.length };
  }
  if (t === "number") {
    const n = value;
    return { kind: "primitive", type: "number", sample: numberSample(n) };
  }
  if (t === "boolean") {
    return { kind: "primitive", type: "boolean", sample: String(value) };
  }
  if (t === "bigint") {
    return { kind: "primitive", type: "bigint", sample: `${value.toString()}n` };
  }
  if (t === "symbol") {
    const desc = value.description ?? "";
    return { kind: "primitive", type: "symbol", sample: redactString(desc, "development") };
  }
  if (t === "function") {
    const fn = value;
    const name = typeof fn.name === "string" && fn.name.length > 0 ? fn.name : void 0;
    return name === void 0 ? { kind: "function", type: "function" } : { kind: "function", type: "function", name };
  }
  const obj = value;
  if (seen.has(obj)) {
    return { kind: "object", type: "object", keys: 0, sample: { __cycle__: { kind: "primitive", type: "string", sample: CIRCULAR, length: CIRCULAR.length } } };
  }
  seen.add(obj);
  if (Array.isArray(value)) {
    const arr = value;
    const nonFiniteIndices = [];
    for (let i = 0; i < arr.length && i < budget.maxKeys; i++) {
      const v = arr[i];
      if (typeof v === "number" && !Number.isFinite(v)) nonFiniteIndices.push(i);
    }
    return nonFiniteIndices.length > 0 ? { kind: "array", type: "array", length: arr.length, nonFiniteIndices } : { kind: "array", type: "array", length: arr.length };
  }
  const tag = Object.prototype.toString.call(value);
  if (tag === "[object Date]" || tag === "[object RegExp]" || tag === "[object Map]" || tag === "[object Set]" || tag === "[object Error]") {
    return { kind: "object", type: "object", keys: 0 };
  }
  if (depth >= budget.maxDepth || budget.nodes >= budget.maxNodes) {
    let keysCount = 0;
    try {
      keysCount = Object.getOwnPropertyNames(obj).length;
    } catch {
      keysCount = 0;
    }
    return { kind: "object", type: "object", keys: keysCount };
  }
  let keys;
  try {
    keys = Object.getOwnPropertyNames(obj);
  } catch {
    return { kind: "object", type: "object", keys: 0 };
  }
  const totalKeys = keys.length;
  const limit = Math.min(keys.length, budget.maxKeys);
  const sample = /* @__PURE__ */ Object.create(null);
  for (let i = 0; i < limit; i++) {
    if (budget.nodes >= budget.maxNodes) break;
    const k = keys[i];
    if (k === "__proto__" || k === "constructor" || k === "prototype") continue;
    let v;
    try {
      const desc = Object.getOwnPropertyDescriptor(obj, k);
      if (desc === void 0) continue;
      if ("get" in desc && desc.get !== void 0) {
        sample[k] = { kind: "primitive", type: "string", sample: "[getter]", length: 8 };
        continue;
      }
      v = desc.value;
    } catch {
      continue;
    }
    sample[k] = walkReceived(v, depth + 1, seen, budget);
  }
  return { kind: "object", type: "object", keys: totalKeys, sample };
}
function sampleString(s) {
  if (detectPii(s)) return PII_REDACTED;
  if (s.length <= SAMPLE_LEN) return s;
  return s.slice(0, SAMPLE_LEN) + "\u2026";
}
function numberSample(n) {
  if (Number.isNaN(n)) return "NaN";
  if (n === Number.POSITIVE_INFINITY) return "Infinity";
  if (n === Number.NEGATIVE_INFINITY) return "-Infinity";
  const s = String(n);
  return s.length > SAMPLE_LEN ? s.slice(0, SAMPLE_LEN) + "\u2026" : s;
}
function redactExpected(value, mode) {
  if (mode === "production") return void 0;
  if (value === void 0) return void 0;
  if (typeof value === "string") {
    return { kind: "predicate", description: redactString(value, mode) };
  }
  switch (value.kind) {
    case "type":
      return { kind: "type", expected: redactString(value.expected, mode) };
    case "enum":
      return { kind: "enum", valid: value.valid.map((v) => redactString(v, mode)) };
    case "range": {
      const out = { kind: "range" };
      const r = out;
      if (typeof value.min === "number") r.min = value.min;
      if (typeof value.max === "number") r.max = value.max;
      if (typeof value.inclusive === "boolean") r.inclusive = value.inclusive;
      return out;
    }
    case "shape": {
      const required = value.required.map((s) => redactString(s, mode));
      const out = value.optional !== void 0 ? { kind: "shape", required, optional: value.optional.map((s) => redactString(s, mode)) } : { kind: "shape", required };
      return out;
    }
    case "predicate":
      return { kind: "predicate", description: redactString(value.description, mode) };
    /* istanbul ignore next */
    default:
      return void 0;
  }
}
var RE_V8 = /^\s*at\s+(?:.*?\s+\()?(?<url>[^()\s]+):(?<line>\d+):(?<col>\d+)\)?\s*$/;
var RE_JSC = /^(?<fn>[^@\s]*)@(?<url>[^\s]+):(?<line>\d+):(?<col>\d+)\s*$/;
function detectEngine(stack) {
  if (/^Error\b/m.test(stack) && /\n\s*at\s/.test(stack)) return "v8";
  if (/@[^\n]*:\d+:\d+/.test(stack)) return "jsc";
  return "unknown";
}
function isLibFrame(url) {
  return /node_modules\/@norkron\//.test(url) || /\/@norkron\/[^/]+\//.test(url);
}
function redactStack(stack, mode) {
  if (mode === "production") return PROD_STACK;
  if (typeof stack !== "string" || stack.length === 0) return "";
  const engine = detectEngine(stack);
  if (engine === "unknown") return "[norkron-stack:unknown-engine]";
  const lines = stack.split("\n");
  const out = [];
  let kept = 0;
  if (engine === "v8" && lines.length > 0 && !/^\s*at\s/.test(lines[0])) {
    out.push(lines[0]);
  }
  const re = engine === "v8" ? RE_V8 : RE_JSC;
  for (let i = engine === "v8" && out.length === 1 ? 1 : 0; i < lines.length; i++) {
    if (kept >= DEFAULT_MAX_STACK_FRAMES_DEV) break;
    const line = lines[i];
    const m = line.match(re);
    if (!m || !m.groups) continue;
    const url = m.groups["url"];
    if (isLibFrame(url)) continue;
    out.push(line);
    kept++;
  }
  return out.join("\n");
}
function applyDevOnlyDescriptors(obj, fields, mode) {
  if (mode !== "production") return;
  for (const f of fields) {
    try {
      delete obj[f];
    } catch {
      try {
        Object.defineProperty(obj, f, {
          value: void 0,
          writable: false,
          enumerable: false,
          configurable: false
        });
      } catch {
      }
    }
  }
}

// src/siphash.ts
var mkU64 = (hi, lo) => ({ hi: hi >>> 0, lo: lo >>> 0 });
function rotl(v, n) {
  if (n === 32) {
    return { hi: v.lo, lo: v.hi };
  }
  if (n < 32) {
    const hi2 = (v.hi << n | v.lo >>> 32 - n) >>> 0;
    const lo2 = (v.lo << n | v.hi >>> 32 - n) >>> 0;
    return { hi: hi2, lo: lo2 };
  }
  const m = n - 32;
  const hi = (v.lo << m | v.hi >>> 32 - m) >>> 0;
  const lo = (v.hi << m | v.lo >>> 32 - m) >>> 0;
  return { hi, lo };
}
function add(a, b) {
  const lo = a.lo + b.lo >>> 0;
  const carry = lo < a.lo ? 1 : 0;
  const hi = a.hi + b.hi + carry >>> 0;
  return { hi, lo };
}
function xor(a, b) {
  return { hi: (a.hi ^ b.hi) >>> 0, lo: (a.lo ^ b.lo) >>> 0 };
}
function sipround(s) {
  s.v0 = add(s.v0, s.v1);
  s.v1 = rotl(s.v1, 13);
  s.v1 = xor(s.v1, s.v0);
  s.v0 = rotl(s.v0, 32);
  s.v2 = add(s.v2, s.v3);
  s.v3 = rotl(s.v3, 16);
  s.v3 = xor(s.v3, s.v2);
  s.v0 = add(s.v0, s.v3);
  s.v3 = rotl(s.v3, 21);
  s.v3 = xor(s.v3, s.v0);
  s.v2 = add(s.v2, s.v1);
  s.v1 = rotl(s.v1, 17);
  s.v1 = xor(s.v1, s.v2);
  s.v2 = rotl(s.v2, 32);
}
function readU64LE(bytes, off) {
  const lo = (bytes[off] | bytes[off + 1] << 8 | bytes[off + 2] << 16 | bytes[off + 3] << 24) >>> 0;
  const hi = (bytes[off + 4] | bytes[off + 5] << 8 | bytes[off + 6] << 16 | bytes[off + 7] << 24) >>> 0;
  return { hi, lo };
}
function siphash24(key2, data) {
  if (key2.length !== 16) {
    throw new Error("siphash key must be exactly 16 bytes");
  }
  const k0 = readU64LE(key2, 0);
  const k1 = readU64LE(key2, 8);
  const s = {
    v0: xor(mkU64(1936682341, 1886610805), k0),
    v1: xor(mkU64(1685025377, 1852075885), k1),
    v2: xor(mkU64(1819895653, 1852142177), k0),
    v3: xor(mkU64(1952801890, 2037671283), k1)
  };
  const len = data.length;
  const wholeBlocks = len & -8;
  let off = 0;
  for (; off < wholeBlocks; off += 8) {
    const m2 = readU64LE(data, off);
    s.v3 = xor(s.v3, m2);
    sipround(s);
    sipround(s);
    s.v0 = xor(s.v0, m2);
  }
  const remaining = len - wholeBlocks;
  let tailLo = 0;
  let tailHi = 0;
  for (let i = 0; i < remaining; i++) {
    const b = data[off + i];
    if (i < 4) {
      tailLo = (tailLo | b << i * 8) >>> 0;
    } else {
      tailHi = (tailHi | b << (i - 4) * 8) >>> 0;
    }
  }
  tailHi = (tailHi | (len & 255) << 24) >>> 0;
  const m = { hi: tailHi, lo: tailLo };
  s.v3 = xor(s.v3, m);
  sipround(s);
  sipround(s);
  s.v0 = xor(s.v0, m);
  s.v2 = xor(s.v2, mkU64(0, 255));
  sipround(s);
  sipround(s);
  sipround(s);
  sipround(s);
  return xor(xor(s.v0, s.v1), xor(s.v2, s.v3));
}
var utf8Encoder = /* @__PURE__ */ new TextEncoder();
function siphash24Hex(key2, input) {
  const bytes = utf8Encoder.encode(input);
  const out = siphash24(key2, bytes);
  const hi = out.hi.toString(16).padStart(8, "0");
  const lo = out.lo.toString(16).padStart(8, "0");
  return hi + lo;
}
function deriveKeyFromSeed(seed) {
  const refKeyA = new Uint8Array([
    78,
    111,
    114,
    107,
    114,
    111,
    110,
    33,
    101,
    114,
    114,
    111,
    114,
    115,
    75,
    65
  ]);
  const refKeyB = new Uint8Array([
    78,
    111,
    114,
    107,
    114,
    111,
    110,
    33,
    101,
    114,
    114,
    111,
    114,
    115,
    75,
    66
  ]);
  const data = utf8Encoder.encode(seed);
  const a = siphash24(refKeyA, data);
  const b = siphash24(refKeyB, data);
  const out = new Uint8Array(16);
  out[0] = a.lo & 255;
  out[1] = a.lo >>> 8 & 255;
  out[2] = a.lo >>> 16 & 255;
  out[3] = a.lo >>> 24 & 255;
  out[4] = a.hi & 255;
  out[5] = a.hi >>> 8 & 255;
  out[6] = a.hi >>> 16 & 255;
  out[7] = a.hi >>> 24 & 255;
  out[8] = b.lo & 255;
  out[9] = b.lo >>> 8 & 255;
  out[10] = b.lo >>> 16 & 255;
  out[11] = b.lo >>> 24 & 255;
  out[12] = b.hi & 255;
  out[13] = b.hi >>> 8 & 255;
  out[14] = b.hi >>> 16 & 255;
  out[15] = b.hi >>> 24 & 255;
  return out;
}
function randomKey() {
  const k = new Uint8Array(16);
  const c = typeof crypto !== "undefined" ? crypto : globalThis.crypto;
  if (c && typeof c.getRandomValues === "function") {
    c.getRandomValues(k);
    return k;
  }
  for (let i = 0; i < 16; i++) {
    k[i] = Math.floor(Math.random() * 256);
  }
  return k;
}
function constantTimeEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

// src/als-loader.ts
var cached;
function loadAls() {
  if (cached !== void 0) return cached;
  const isNodeLike2 = typeof process !== "undefined" && typeof process.versions?.node === "string";
  if (!isNodeLike2) {
    cached = null;
    return null;
  }
  try {
    const im = import.meta.require;
    if (typeof im === "function") {
      const mod2 = im("node:async_hooks");
      cached = mod2?.AsyncLocalStorage ?? null;
      return cached;
    }
    const req = globalThis.require;
    if (typeof req === "function") {
      const mod2 = req("node:async_hooks");
      cached = mod2?.AsyncLocalStorage ?? null;
      return cached;
    }
    const loadSync = new Function(
      'return require("node:module").createRequire(import.meta.url)("node:async_hooks")'
    );
    const mod = loadSync();
    cached = mod?.AsyncLocalStorage ?? null;
    return cached;
  } catch {
    cached = null;
    return null;
  }
}

// src/correlation.ts
var key = null;
var seedSet = false;
var seedString = null;
var browserCurrentId = null;
var provider = null;
var isNodeLike = typeof process !== "undefined" && typeof process.versions?.node === "string";
var als = (() => {
  const Ctor = loadAls();
  return Ctor ? new Ctor() : null;
})();
function tryLoadAls() {
  return als;
}
var BAD_PREFIXES = [
  "00000000",
  "0000000000000000",
  "aaaaaaaa",
  "abcdefab",
  "12345678",
  "deadbeef",
  "cafebabe"
];
function shannonEntropy(s) {
  if (s.length === 0) return 0;
  const counts = /* @__PURE__ */ new Map();
  for (const ch of s) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  let h = 0;
  const n = s.length;
  for (const c of counts.values()) {
    const p = c / n;
    h -= p * Math.log2(p);
  }
  return h;
}
function isSequential(s) {
  let runs = 0;
  let runStart = 0;
  for (let i = 1; i <= s.length; i++) {
    if (i === s.length || s.charCodeAt(i) !== s.charCodeAt(i - 1) + 1) {
      const len = i - runStart;
      if (len >= 6) runs += len;
      runStart = i;
    }
  }
  return runs >= s.length * 0.8;
}
function validateSeed(seed) {
  if (typeof seed !== "string") {
    throw new Error("correlation seed must be a string");
  }
  if (seed.length < 32) {
    throw new Error(`correlation seed too short (${seed.length} chars; require \u226532)`);
  }
  const entropy = shannonEntropy(seed);
  if (entropy < 3) {
    throw new Error(`correlation seed entropy too low (${entropy.toFixed(2)} bits/char; require \u22653.0)`);
  }
  const lower = seed.toLowerCase();
  for (const p of BAD_PREFIXES) {
    if (lower.startsWith(p)) {
      throw new Error(`correlation seed has known-weak prefix: ${p}`);
    }
  }
  if (isSequential(seed)) {
    throw new Error("correlation seed is sequential (e.g. abcdef...); use random bytes");
  }
}
function setCorrelationSeed(seed) {
  validateSeed(seed);
  const newKey = deriveKeyFromSeed(seed);
  if (seedSet) {
    key !== null && constantTimeEqual(key, newKey);
    throw new Error("correlation seed already set (write-once per process)");
  }
  key = newKey;
  seedSet = true;
  seedString = seed;
}
function getCorrelationSeed() {
  return seedString;
}
function ensureKey() {
  if (key === null) key = randomKey();
  return key;
}
function deriveCorrelationId(input) {
  const k = ensureKey();
  return siphash24Hex(k, input);
}
function getCurrentCorrelationId() {
  if (provider) {
    const fromProvider = provider.getCurrent();
    if (fromProvider) return fromProvider;
  }
  if (isNodeLike) {
    const a = tryLoadAls();
    if (a) {
      const stored = a.getStore();
      if (stored) return stored;
    }
  }
  if (browserCurrentId) return browserCurrentId;
  return deriveCorrelationId(`anon:${Date.now()}:${Math.random()}`);
}
function withCorrelation(id, fn) {
  if (isNodeLike) {
    const a = tryLoadAls();
    if (a) return a.run(id, fn);
  }
  const prev = browserCurrentId;
  browserCurrentId = id;
  try {
    return fn();
  } finally {
    browserCurrentId = prev;
  }
}
function withCorrelationAsync(id, fn) {
  if (isNodeLike) {
    const a = tryLoadAls();
    if (a) return a.run(id, fn);
  }
  const prev = browserCurrentId;
  browserCurrentId = id;
  return fn().finally(() => {
    browserCurrentId = prev;
  });
}
function useCorrelationBoundary() {
  const id = deriveCorrelationId(`boundary:${Date.now()}:${Math.random()}`);
  return {
    id,
    correlate: (fn) => withCorrelation(id, fn),
    correlateAsync: (fn) => withCorrelationAsync(id, fn)
  };
}
function setCorrelationProvider(p) {
  provider = p;
}
function serializeCorrelation() {
  const id = getCurrentCorrelationId();
  return `nrk1:${id}`;
}
function adoptCorrelation(serialized) {
  if (typeof serialized !== "string" || !serialized.startsWith("nrk1:")) {
    throw new Error("adoptCorrelation: invalid wire format (expected nrk1:<id>)");
  }
  const id = serialized.slice("nrk1:".length);
  if (id.length === 0) {
    throw new Error("adoptCorrelation: empty id");
  }
  if (!isNodeLike || !tryLoadAls()) {
    browserCurrentId = id;
  }
  return id;
}
function scheduleWithCorrelation(cb, ms) {
  const captured = getCurrentCorrelationId();
  return setTimeout(() => {
    withCorrelation(captured, cb);
  }, ms);
}
function rafWithCorrelation(cb) {
  const captured = getCurrentCorrelationId();
  const raf = typeof requestAnimationFrame !== "undefined" ? requestAnimationFrame : (fn) => setTimeout(fn, 0);
  return raf(() => {
    withCorrelation(captured, cb);
  });
}

// src/frequency.ts
var map = /* @__PURE__ */ new Map();
var head = null;
var tail = null;
var cap = 1e3;
var defaultWindowMs = 6e4;
var lastClockReading = 0;
var clockNonMonotonicWarned = false;
function readClockMonotonic() {
  const perf = typeof performance !== "undefined" ? performance : globalThis.performance;
  let now = perf?.now?.() ?? Date.now();
  if (now < lastClockReading) {
    if (!clockNonMonotonicWarned) {
      clockNonMonotonicWarned = true;
      console.error(
        `[norkron/errors:meta] CLOCK_NON_MONOTONIC: ${lastClockReading} \u2192 ${now} (clamped)`
      );
    }
    now = lastClockReading;
  } else {
    lastClockReading = now;
  }
  return now;
}
function __resetClockState() {
  lastClockReading = 0;
  clockNonMonotonicWarned = false;
}
function detach(e) {
  if (e.prev) e.prev.next = e.next;
  else head = e.next;
  if (e.next) e.next.prev = e.prev;
  else tail = e.prev;
  e.prev = null;
  e.next = null;
}
function pushHead(e) {
  e.prev = null;
  e.next = head;
  if (head) head.prev = e;
  head = e;
  if (!tail) tail = e;
}
function promote(e) {
  if (head === e) return;
  detach(e);
  pushHead(e);
}
function evictTail() {
  if (!tail) return;
  const evicted = tail;
  detach(evicted);
  map.delete(evicted.key);
}
function bumpFrequency(key2) {
  const now = readClockMonotonic();
  const existing = map.get(key2);
  if (existing) {
    if (now - existing.firstFiredAt > existing.windowMs) {
      existing.count = 1;
      existing.firstFiredAt = now;
      existing.firstFiredAtWall = Date.now();
    } else {
      existing.count++;
    }
    existing.lastFiredAt = now;
    promote(existing);
    return { count: existing.count, windowMs: existing.windowMs };
  }
  if (map.size >= cap) evictTail();
  const e = {
    key: key2,
    count: 1,
    firstFiredAt: now,
    lastFiredAt: now,
    firstFiredAtWall: Date.now(),
    windowMs: defaultWindowMs,
    prev: null,
    next: null
  };
  map.set(key2, e);
  pushHead(e);
  return { count: 1, windowMs: defaultWindowMs };
}
function getFrequencySnapshot(key2) {
  const e = map.get(key2);
  if (!e) return null;
  return {
    count: e.count,
    windowMs: e.windowMs,
    firstFiredAt: e.firstFiredAtWall,
    lastFiredAt: e.lastFiredAt
  };
}
function clearFrequencyCache() {
  map.clear();
  head = null;
  tail = null;
  __resetClockState();
}

// src/source-map.ts
var REDACTED_STACK_VALUE = "[norkron-stack:redacted]";
function getRedactedStackString() {
  return REDACTED_STACK_VALUE;
}
var DEFAULT_MAX_FRAMES = 64;
var config = {
  enabled: true,
  maxFrames: DEFAULT_MAX_FRAMES
};
function configureSourceMap(opts) {
  if (typeof opts.enabled === "boolean") config.enabled = opts.enabled;
  if (typeof opts.maxFrames === "number" && opts.maxFrames > 0) {
    config.maxFrames = Math.min(opts.maxFrames, 256);
  }
}
function readMode() {
  const g = globalThis;
  const sentinel = g.__NORKRON_ERRORS_MODE__;
  if (sentinel === "production" || sentinel === "development" || sentinel === "test") {
    return sentinel;
  }
  if (typeof process !== "undefined" && process.env) {
    const envMode = process.env.NORKRON_ERRORS_MODE;
    if (envMode === "production" || envMode === "development" || envMode === "test") {
      return envMode;
    }
    if (process.env.NODE_ENV === "production") return "production";
    if (process.env.NODE_ENV === "test") return "test";
  }
  return "development";
}
function captureSourceLocation() {
  if (!config.enabled) return void 0;
  const mode = readMode();
  if (mode === "production") return void 0;
  const walker = __getDevWalker();
  if (!walker) return void 0;
  const err = new Error();
  const stack = err.stack;
  if (typeof stack !== "string") return void 0;
  return walker.locateConsumerFrame(stack, config.maxFrames);
}
var devWalker;
function __getDevWalker() {
  if (devWalker) return devWalker;
  devWalker = createBuiltinDevWalker();
  return devWalker;
}
var STACK_REGEXES = {
  // V8: `    at fnName (file:line:col)` OR `    at file:line:col`
  v8: /^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/,
  // JavaScriptCore (Safari): `fnName@file:line:col`
  jsc: /^(.*?)@(.+?):(\d+):(\d+)$/,
  // SpiderMonkey (Firefox): same shape as JSC
  spidermonkey: /^(.*?)@(.+?):(\d+):(\d+)$/,
  // Hermes (RN): `at fn (file:line:col)` (similar to v8 but slightly different)
  hermes: /^\s*at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)$/
};
function detectEngine2(stack) {
  if (/^\s*at\s+/m.test(stack)) {
    if (typeof process !== "undefined" && process.versions && process.versions.node) {
      return "v8";
    }
    if (typeof navigator !== "undefined" && /Chrome|Edg|Bun/i.test(navigator.userAgent || "")) {
      return "v8";
    }
    if (typeof navigator !== "undefined" && /hermes/i.test(navigator.userAgent || "")) {
      return "hermes";
    }
    return "v8";
  }
  if (/^[^@\n]*@[^\n]+:\d+:\d+/m.test(stack)) {
    if (typeof navigator !== "undefined" && /Firefox/i.test(navigator.userAgent || "")) {
      return "spidermonkey";
    }
    return "jsc";
  }
  return "unknown";
}
function stripFrameArgs(rawFrame) {
  const locMatch = /\(([^()]*:\d+:\d+)\)\s*$/.exec(rawFrame);
  if (!locMatch || locMatch.index === void 0) {
    return rawFrame.replace(/\([^()]*\)/g, "()");
  }
  const head2 = rawFrame.slice(0, locMatch.index);
  const tail2 = rawFrame.slice(locMatch.index);
  return head2.replace(/\([^()]*\)/g, "()") + tail2;
}
function parseFrame(rawFrame, engine) {
  if (engine === "unknown") return void 0;
  const regex = STACK_REGEXES[engine];
  const sanitized = stripFrameArgs(rawFrame);
  const m = regex.exec(sanitized);
  if (!m) return void 0;
  const fnName = m[1] || void 0;
  const file = m[2];
  const line = Number(m[3]);
  const col = Number(m[4]);
  if (file === void 0 || !file || !Number.isFinite(line) || !Number.isFinite(col)) return void 0;
  return { raw: sanitized, fnName, file, line, col };
}
function walkFrames(stack, maxFrames = DEFAULT_MAX_FRAMES) {
  const cap2 = Math.min(Math.max(1, maxFrames), 64);
  const engine = detectEngine2(stack);
  if (engine === "unknown") return [];
  const lines = stack.split("\n");
  const frames = [];
  for (let i = 0; i < lines.length && frames.length < cap2; i++) {
    const lineRaw = lines[i];
    if (lineRaw === void 0) continue;
    const line = lineRaw.trim();
    if (!line) continue;
    const frame = parseFrame(line, engine);
    if (frame) frames.push(frame);
  }
  return frames;
}
function isNorkronInternalFrame(file) {
  if (!file) return true;
  if (/[\\/]node_modules[\\/]@norkron[\\/]/.test(file)) return true;
  if (/[\\/]libs[\\/]errors[\\/]/.test(file)) return true;
  if (/source-map(\.|-walker)|panic\.[tj]s|warn\.[tj]s|errors\.[tj]s/.test(file)) return true;
  return false;
}
function createBuiltinDevWalker() {
  return {
    locateConsumerFrame(stack, maxFrames) {
      const frames = walkFrames(stack, maxFrames);
      for (const f of frames) {
        if (isNorkronInternalFrame(f.file)) continue;
        return {
          file: f.file,
          line: f.line,
          col: f.col
        };
      }
      return void 0;
    }
  };
}

// src/errors.ts
var INSPECT_CUSTOM2 = /* @__PURE__ */ Symbol.for("nodejs.util.inspect.custom");
var PROD_STACK2 = "[norkron-stack:redacted]";
var NorkronError = class extends Error {
  name = "NorkronError";
  kind = "contract";
  mode;
  lib;
  fn;
  code;
  correlationId;
  timestamp;
  frequency;
  severity;
  actionLabels;
  // Dev/test-only — removed via property-descriptor redaction in prod.
  prop;
  received;
  expected;
  causeHint;
  actions;
  reproTest;
  sourceLocation;
  relatedIds;
  constructor(opts) {
    const causeChain = sanitizeCause(opts.errorCause);
    super(opts.message, causeChain !== void 0 ? { cause: causeChain } : void 0);
    const mode = getNorkronErrorsMode();
    this.mode = mode;
    this.lib = opts.lib;
    this.fn = opts.fn;
    this.code = opts.code;
    this.severity = opts.severity;
    this.correlationId = getCurrentCorrelationId();
    this.timestamp = Date.now();
    this.frequency = bumpFrequency(`${opts.lib}:${opts.fn}:${opts.code}`);
    this.actionLabels = opts.actions ? opts.actions.map((a) => a.label) : [];
    if (opts.prop !== void 0) this.prop = opts.prop;
    if (opts.received !== void 0) {
      this.received = redactReceived(opts.received, mode);
    }
    if (opts.expected !== void 0) {
      const exp = redactExpected(opts.expected, mode);
      if (exp !== void 0) this.expected = exp;
    }
    if (opts.causeHint !== void 0) this.causeHint = opts.causeHint;
    if (opts.actions !== void 0) {
      this.actions = opts.actions.map((a) => ({
        label: a.label,
        severity: a.severity,
        description: a.description,
        ...a.docsAnchor !== void 0 ? { docsAnchor: a.docsAnchor } : {}
      }));
    }
    if (opts.reproTest !== void 0) this.reproTest = opts.reproTest;
    if (opts.related !== void 0 && opts.related.length > 0) {
      this.relatedIds = opts.related;
    }
    const loc = captureSourceLocation();
    if (loc !== void 0) this.sourceLocation = loc;
    Object.defineProperty(this, "__norkron", {
      value: true,
      writable: false,
      configurable: false,
      enumerable: false
    });
    void this.stack;
    if (mode === "production") {
      Object.defineProperty(this, "stack", {
        value: PROD_STACK2,
        writable: false,
        configurable: false,
        enumerable: false
      });
    } else {
      const cur = this.stack;
      if (cur !== void 0) {
        const redacted = redactStack(cur, mode);
        if (redacted !== cur) {
          Object.defineProperty(this, "stack", {
            value: redacted,
            writable: false,
            configurable: true,
            enumerable: false
          });
        }
      }
    }
    applyDevOnlyDescriptors(
      this,
      ["prop", "received", "expected", "causeHint", "actions", "reproTest", "sourceLocation", "relatedIds"],
      mode
    );
  }
  // ── Symbol.toPrimitive (§25.2.F) ────────────────────────────────────────
  [Symbol.toPrimitive](_hint) {
    return this.toString();
  }
  // ── util.inspect (Node) custom hook (§25.2.C) ───────────────────────────
  [INSPECT_CUSTOM2]() {
    return this.toString();
  }
  toString() {
    if (this.mode === "production") {
      return `[${this.lib}] ${this.fn}: ${this.code} (correlation: ${this.correlationId})`;
    }
    const propPart = this.prop !== void 0 ? ` prop=${this.prop}` : "";
    const recv = this.received;
    const recvPart = recv !== void 0 ? ` received=${summarize(recv)}` : "";
    return `[${this.lib}] ${this.fn}: ${this.code} (${this.kind})${propPart}${recvPart} \u2014 ${this.message}`;
  }
  toJSON() {
    const base = {
      __schemaVersion: 1,
      mode: this.mode,
      lib: this.lib,
      fn: this.fn,
      code: this.code,
      correlationId: this.correlationId,
      timestamp: this.timestamp,
      frequency: this.frequency,
      actionLabels: this.actionLabels,
      severity: this.severity
    };
    if (this.mode === "production") {
      return base;
    }
    const out = { ...base };
    if (this.prop !== void 0) out.prop = this.prop;
    if (this.received !== void 0) {
      out.received = this.received;
    }
    if (this.expected !== void 0) out.expected = this.expected;
    if (this.causeHint !== void 0) out.causeHint = this.causeHint;
    if (this.actions !== void 0) out.actions = this.actions;
    if (this.reproTest !== void 0) out.reproTest = this.reproTest;
    if (this.sourceLocation !== void 0) out.sourceLocation = this.sourceLocation;
    if (this.relatedIds !== void 0) out.relatedIds = this.relatedIds;
    return out;
  }
};
Object.defineProperty(NorkronError.prototype, "__norkron", {
  value: true,
  writable: false,
  configurable: false,
  enumerable: false
});
var NorkronRangeError = class extends NorkronError {
  name = "NorkronRangeError";
  kind = "range";
};
var NorkronTypeError = class extends NorkronError {
  name = "NorkronTypeError";
  kind = "type";
};
var NorkronSyntaxError = class extends NorkronError {
  name = "NorkronSyntaxError";
  kind = "syntax";
};
var NorkronStateError = class extends NorkronError {
  name = "NorkronStateError";
  kind = "state";
};
var NorkronContractError = class extends NorkronError {
  name = "NorkronContractError";
  kind = "contract";
};
var NorkronEnvironmentError = class extends NorkronError {
  name = "NorkronEnvironmentError";
  kind = "environment";
};
var NorkronVersionError = class extends NorkronError {
  name = "NorkronVersionError";
  kind = "version";
};
var NorkronSecurityError = class extends NorkronError {
  name = "NorkronSecurityError";
  kind = "security";
};
var NorkronLockedModeError = class extends NorkronError {
  name = "NorkronLockedModeError";
  kind = "locked-mode";
};
function constructErrorByKind(kind, opts) {
  switch (kind) {
    case "range":
      return new NorkronRangeError({ ...opts, kind });
    case "type":
      return new NorkronTypeError({ ...opts, kind });
    case "syntax":
      return new NorkronSyntaxError({ ...opts, kind });
    case "state":
      return new NorkronStateError({ ...opts, kind });
    case "environment":
      return new NorkronEnvironmentError({ ...opts, kind });
    case "version":
      return new NorkronVersionError({ ...opts, kind });
    case "security":
      return new NorkronSecurityError({ ...opts, kind });
    case "locked-mode":
      return new NorkronLockedModeError({ ...opts, kind });
    case "contract":
    default:
      return new NorkronContractError({ ...opts, kind: "contract" });
  }
}
function sanitizeCause(cause, maxDepth) {
  if (cause === void 0 || cause === null) return void 0;
  const mode = getNorkronErrorsMode();
  if (mode !== "production") return cause;
  const seen = /* @__PURE__ */ new WeakSet();
  function walk(c, depth) {
    if (c === null || c === void 0) return void 0;
    if (typeof c !== "object") return { code: "OPAQUE_CAUSE" };
    if (seen.has(c)) return { code: "OPAQUE_CAUSE" };
    seen.add(c);
    if (isNorkronInstance(c)) {
      return c;
    }
    return { code: "OPAQUE_CAUSE" };
  }
  return walk(cause);
}
function isNorkronInstance(v) {
  return typeof v === "object" && v !== null && "__norkron" in v && v.__norkron === true;
}
function isReceivedShape(v) {
  return typeof v === "object" && v !== null && "kind" in v && typeof v.kind === "string";
}
function summarize(v) {
  if (v === null) return "null";
  if (v === void 0) return "undefined";
  const t = typeof v;
  if (t === "string") {
    const s = v;
    return s.length > 32 ? `"${s.slice(0, 29)}..."` : `"${s}"`;
  }
  if (t === "number" || t === "boolean" || t === "bigint") return String(v);
  if (Array.isArray(v)) return `array(len=${v.length})`;
  if (t === "object" && isReceivedShape(v)) return `shape(${v.kind})`;
  if (t === "object") return `object(keys=${Object.keys(v).length})`;
  return t;
}

// src/lib-identity.ts
var LIB_TOKENS_SLOT = /* @__PURE__ */ Symbol.for("norkron.errors.libTokens");
var LIB_META_SLOT = /* @__PURE__ */ Symbol.for("norkron.errors.libIdentityMeta");
function getTokens() {
  const g = globalThis;
  let s = g[LIB_TOKENS_SLOT];
  if (!s) {
    s = { tokens: /* @__PURE__ */ new Map() };
    g[LIB_TOKENS_SLOT] = s;
  }
  return s;
}
function getMetaState() {
  const g = globalThis;
  let s = g[LIB_META_SLOT];
  if (!s) {
    s = { metaSeen: /* @__PURE__ */ new Set() };
    g[LIB_META_SLOT] = s;
  }
  return s;
}
function fireMetaWarning(code, payload) {
  const dedup = getMetaState();
  const key2 = `${code}:${payload["lib"] ?? ""}`;
  if (dedup.metaSeen.has(key2)) return;
  dedup.metaSeen.add(key2);
  console.error(`[norkron/errors:meta] ${code}`, payload);
}
function registerLib(libName, token) {
  if (typeof libName !== "string" || libName.length === 0) {
    throw new TypeError("[norkron/errors] registerLib: libName must be a non-empty string");
  }
  if (typeof token !== "symbol") {
    throw new TypeError("[norkron/errors] registerLib: token must be a Symbol");
  }
  const state = getTokens();
  const existing = state.tokens.get(libName);
  if (existing && existing !== token) {
    fireMetaWarning("LIB_IDENTITY_SPOOFING_DETECTED", {
      lib: libName,
      reason: "duplicate-registration-with-divergent-token"
    });
    return;
  }
  state.tokens.set(libName, token);
}
function getLibToken(libName) {
  return getTokens().tokens.get(libName);
}
function validateLibIdentity(libName, providedToken) {
  if (typeof libName !== "string" || libName.length === 0) {
    return { trusted: false, canonicalLib: "@unregistered/<empty>", reason: "unregistered" };
  }
  const registered = getTokens().tokens.get(libName);
  if (!registered) {
    fireMetaWarning("LIB_NAME_UNREGISTERED", { lib: libName });
    return {
      trusted: false,
      canonicalLib: `@unregistered/${libName}`,
      reason: "unregistered"
    };
  }
  if (providedToken === void 0) {
    fireMetaWarning("LIB_IDENTITY_SPOOFING_DETECTED", { lib: libName, reason: "token-missing" });
    return {
      trusted: false,
      canonicalLib: `@unverified/${libName}`,
      reason: "token-missing"
    };
  }
  if (providedToken !== registered) {
    fireMetaWarning("LIB_IDENTITY_SPOOFING_DETECTED", { lib: libName, reason: "token-mismatch" });
    return {
      trusted: false,
      canonicalLib: `@unverified/${libName}`,
      reason: "token-mismatch"
    };
  }
  return { trusted: true, canonicalLib: libName, reason: "ok" };
}
function captureCallerFrames(depth = 8) {
  const stack = new Error().stack;
  if (typeof stack !== "string") return [];
  const lines = stack.split("\n");
  const frames = [];
  for (const line of lines) {
    if (line.includes("@norkron/web-errors") && !line.includes("node_modules/@norkron/errors")) {
      continue;
    }
    if (line.includes("lib-identity") || line.includes("catalog")) continue;
    if (line.startsWith("Error")) continue;
    frames.push(line);
    if (frames.length >= depth) break;
  }
  return frames;
}
function validateCallerStackFrame(libName, capturedFrames) {
  const frames = capturedFrames ?? captureCallerFrames();
  if (frames.length === 0) {
    return { matched: true };
  }
  const altName = libName.replace(/^(@[^/]+\/)web-/, "$1");
  const candidates = altName !== libName ? [libName, altName] : [libName];
  const needles = [];
  for (const name of candidates) {
    needles.push(`node_modules/${name}`, name);
  }
  for (const frame of frames) {
    for (const n of needles) {
      if (frame.includes(n)) return { matched: true };
    }
  }
  fireMetaWarning("LIB_IDENTITY_SPOOFING_DETECTED", {
    lib: libName,
    reason: "stack-frame-mismatch",
    sample: frames[0]
  });
  return { matched: false, reason: "stack-frame-mismatch" };
}
function isNorkronError(value) {
  if (value === null || typeof value !== "object" && typeof value !== "function") return false;
  const v = value;
  if (v["__norkron"] !== true) return false;
  if (typeof v["kind"] !== "string") return false;
  if (typeof v["code"] !== "string") return false;
  if (typeof v["lib"] !== "string") return false;
  return true;
}
function isNorkronWarning(value) {
  if (value === null || typeof value !== "object" && typeof value !== "function") return false;
  const v = value;
  if (v["__norkronWarning"] !== true) return false;
  if (typeof v["code"] !== "string") return false;
  if (typeof v["lib"] !== "string") return false;
  return true;
}

// src/catalog.ts
var CATALOG_SLOT = /* @__PURE__ */ Symbol.for("norkron.errors.catalog");
var META_DEDUP_SLOT = /* @__PURE__ */ Symbol.for("norkron.errors.catalog.metaDedup");
function getState() {
  const g = globalThis;
  let s = g[CATALOG_SLOT];
  if (!s) {
    s = { libs: /* @__PURE__ */ Object.create(null) };
    g[CATALOG_SLOT] = s;
  }
  return s;
}
function getMetaDedup() {
  const g = globalThis;
  let s = g[META_DEDUP_SLOT];
  if (!s) {
    s = { seen: /* @__PURE__ */ new Set() };
    g[META_DEDUP_SLOT] = s;
  }
  return s;
}
function fireMetaWarning2(code, payload) {
  const dedup = getMetaDedup();
  const dedupKey = `${code}:${payload["lib"] ?? ""}:${payload["code"] ?? ""}`;
  if (dedup.seen.has(dedupKey)) return;
  dedup.seen.add(dedupKey);
  console.error(`[norkron/errors:meta] ${code}`, payload);
}
function metaEqual(a, b) {
  return a.description === b.description && a.severity === b.severity && a.docsAnchor === b.docsAnchor && a.exampleFix === b.exampleFix && a.kind === b.kind;
}
var ERROR_CODES = new Proxy({}, {
  get(_t, prop) {
    if (typeof prop !== "string") return void 0;
    const libBucket = getState().libs[prop];
    return libBucket;
  },
  ownKeys() {
    return Object.keys(getState().libs);
  },
  has(_t, prop) {
    if (typeof prop !== "string") return false;
    return prop in getState().libs;
  },
  getOwnPropertyDescriptor(_t, prop) {
    if (typeof prop !== "string") return void 0;
    const libBucket = getState().libs[prop];
    if (!libBucket) return void 0;
    return {
      value: libBucket,
      writable: false,
      enumerable: true,
      configurable: true
    };
  },
  set() {
    return false;
  },
  deleteProperty() {
    return false;
  },
  defineProperty() {
    return false;
  }
});
function registerCodes(lib, codes) {
  if (typeof lib !== "string" || lib.length === 0) {
    throw new TypeError("[norkron/errors] registerCodes: `lib` must be a non-empty string");
  }
  if (codes === null || typeof codes !== "object") {
    throw new TypeError("[norkron/errors] registerCodes: `codes` must be an object");
  }
  const state = getState();
  const existing = state.libs[lib];
  if (!existing) {
    const bucket = /* @__PURE__ */ Object.create(null);
    for (const codeName of Object.keys(codes)) {
      const meta = codes[codeName];
      if (!meta || typeof meta !== "object") {
        throw new TypeError(
          `[norkron/errors] registerCodes: meta for '${lib}.${codeName}' must be an object`
        );
      }
      if (typeof meta.description !== "string" || meta.description.length === 0) {
        throw new TypeError(
          `[norkron/errors] registerCodes: '${lib}.${codeName}' missing description`
        );
      }
      if (typeof meta.docsAnchor !== "string" || meta.docsAnchor.length === 0) {
        throw new TypeError(
          `[norkron/errors] registerCodes: '${lib}.${codeName}' missing docsAnchor`
        );
      }
      bucket[codeName] = Object.freeze({ ...meta });
    }
    state.libs[lib] = Object.freeze(bucket);
    return state.libs[lib];
  }
  let working = null;
  for (const codeName of Object.keys(codes)) {
    const incoming = codes[codeName];
    const current = working ?? existing;
    const previous = current[codeName];
    if (!previous) {
      if (!working) {
        working = /* @__PURE__ */ Object.create(null);
        for (const k of Object.keys(existing)) working[k] = existing[k];
      }
      working[codeName] = Object.freeze({ ...incoming });
      continue;
    }
    if (metaEqual(previous, incoming)) {
      continue;
    }
    fireMetaWarning2("REGISTRY_CODE_COLLISION", {
      lib,
      code: codeName,
      kept: {
        description: previous.description,
        severity: previous.severity,
        docsAnchor: previous.docsAnchor
      },
      rejected: {
        description: incoming.description,
        severity: incoming.severity,
        docsAnchor: incoming.docsAnchor
      }
    });
  }
  if (working) {
    state.libs[lib] = Object.freeze(working);
  }
  return state.libs[lib];
}
function assertCodeRegistered(lib, code) {
  const bucket = getState().libs[lib];
  if (!bucket || !(code in bucket)) {
    const err = new Error(
      `[norkron/errors] code '${code}' is not registered for lib '${lib}'. Call registerCodes('${lib}', { ${code}: { description, severity, docsAnchor } }) at module load.`
    );
    err.code = "CODE_UNREGISTERED";
    throw err;
  }
}
function getCodeMeta(lib, code) {
  return getState().libs[lib]?.[code];
}
function listLibs() {
  return Object.keys(getState().libs);
}
function listCodes(lib) {
  const bucket = getState().libs[lib];
  if (!bucket) return [];
  return Object.keys(bucket);
}
registerCodes("@norkron/web-errors", {
  LOCKED_MODE: {
    description: "setNorkronErrorsMode called after the first error/warning was fired. Mode is locked for process lifetime.",
    severity: "error",
    docsAnchor: "https://norkron.dev/errors/v0/errors/LOCKED_MODE",
    kind: "locked-mode"
  },
  SINK_FAILED: {
    description: "A registered sink threw or returned a rejected promise. Meta-warning is deduped per sink instance.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/SINK_FAILED"
  },
  SINK_TIMEOUT: {
    description: "A sink exceeded the 50ms dispatch budget; remaining sinks still run.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/SINK_TIMEOUT"
  },
  SINK_REGISTRY_FULL: {
    description: "Sink registry exceeded 32 sink cap; new registration rejected.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/SINK_REGISTRY_FULL"
  },
  SOURCEMAP_LEAK_DETECTED: {
    description: "Production-mode error contained a sourcemap-shaped string (file:line:col). Indicates redaction failure.",
    severity: "critical-but-non-throwing",
    docsAnchor: "https://norkron.dev/errors/v0/errors/SOURCEMAP_LEAK_DETECTED",
    kind: "security"
  },
  BUDGET_EXCEEDED: {
    description: "panic()/warn() exceeded 5x its dev/prod budget. Indicates a pathological input (e.g. 10MB received).",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/BUDGET_EXCEEDED"
  },
  MUTATION_SURVIVED: {
    description: "Stryker reported a surviving mutation in @norkron/errors. Surfaces in CI, not at runtime.",
    severity: "info",
    docsAnchor: "https://norkron.dev/errors/v0/errors/MUTATION_SURVIVED"
  },
  REGISTRY_CODE_COLLISION: {
    description: "registerCodes called twice for the same (lib, code) with divergent metadata. First registration wins.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/REGISTRY_CODE_COLLISION"
  },
  VERSION_MISMATCH: {
    description: "Two copies of @norkron/errors initialized in the same process. First-loaded wins; second forwards.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/VERSION_MISMATCH",
    kind: "version"
  },
  LIB_NAME_UNREGISTERED: {
    description: "panic({ lib }) called with a lib name not registered via registerLib. Error tagged @unregistered/<name>.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/LIB_NAME_UNREGISTERED"
  },
  LIB_IDENTITY_SPOOFING_DETECTED: {
    description: "Caller frame URL did not match the registered lib namespace. Error tagged @unverified/<name>.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/LIB_IDENTITY_SPOOFING_DETECTED",
    kind: "security"
  },
  CODE_UNREGISTERED: {
    description: "panic({ code }) called with a code not present in registerCodes for the named lib.",
    severity: "error",
    docsAnchor: "https://norkron.dev/errors/v0/errors/CODE_UNREGISTERED"
  },
  PEER_DEP_VERSION_MISMATCH: {
    description: "A consuming lib was built against an incompatible @norkron/errors major.",
    severity: "error",
    docsAnchor: "https://norkron.dev/errors/v0/errors/PEER_DEP_VERSION_MISMATCH",
    kind: "version"
  },
  BURST_THRESHOLD_EXCEEDED: {
    description: "Frequency tracker observed >N events within window; subsequent events deduped.",
    severity: "info",
    docsAnchor: "https://norkron.dev/errors/v0/errors/BURST_THRESHOLD_EXCEEDED"
  },
  DEDUP_CACHE_EVICTION: {
    description: "LRU dedup cache evicted an entry; identical errors may re-fire.",
    severity: "info",
    docsAnchor: "https://norkron.dev/errors/v0/errors/DEDUP_CACHE_EVICTION"
  },
  WEB_INVALID_INPUT_SHAPE: {
    description: "Universal payload shape for `<LIB>_INVALID_INPUT` runtime validation failures emitted by every web lib whose zod schema rejected an input. Each warning carries { fieldPath: string[], expected: string, received: unknown }. Per-lib codes (e.g. WEB_TOOLTIP_INVALID_INPUT) route through `reportInvalidInput()`. See feedback_zod_mandatory.md.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/WEB_INVALID_INPUT_SHAPE",
    exampleFix: "Pass input matching the lib`s exported zod schema. Inspect payload.fieldPath + payload.expected to locate the offending field."
  }
});
function isCodeRegistered(lib, code) {
  try {
    assertCodeRegistered(lib, code);
    return true;
  } catch {
    return false;
  }
}

// src/sink.ts
var MAX_SINKS = 32;
var SINK_TIMEOUT_MS = 50;
var SUPPORTED_SCHEMA_VERSION = 1;
var sinks = [];
var nextSinkId = 1;
var inFlightDispatches = /* @__PURE__ */ new Set();
var defaultMetaWarningHandler = (warning) => {
  try {
    console.warn(`[@norkron/errors:${warning.code}] ${warning.message}`);
  } catch {
  }
};
var metaWarningHandler = defaultMetaWarningHandler;
function setMetaWarningHandler(handler) {
  metaWarningHandler = handler;
}
function emitMetaWarning(warning) {
  try {
    const h = metaWarningHandler;
    const out = h(warning);
    if (out !== void 0) void out;
  } catch {
    try {
      metaWarningHandler(
        `[${warning.code}] ${warning.message}`
      );
    } catch {
    }
  }
}
var noopUnsubscribe = () => {
};
function setSink(sink) {
  if (sinks.length >= MAX_SINKS) {
    emitMetaWarning({
      code: "TOO_MANY_SINKS",
      message: `Sink registry cap (${MAX_SINKS}) reached; ignoring registration.`
    });
    return noopUnsubscribe;
  }
  const id = nextSinkId++;
  sinks.push({ id, sink });
  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    const idx = sinks.findIndex((r) => r.id === id);
    if (idx !== -1) sinks.splice(idx, 1);
  };
}
function clearSinks() {
  sinks.length = 0;
}
async function callWithTimeout(invoke) {
  let result;
  try {
    result = invoke();
  } catch (err) {
    return { error: err };
  }
  if (!isPromiseLike(result)) return "ok";
  let timer;
  const timeoutPromise = new Promise((resolve) => {
    timer = setTimeout(() => resolve("timeout"), SINK_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      (async () => {
        try {
          await result;
          return "ok";
        } catch (err) {
          return { error: err };
        }
      })(),
      timeoutPromise
    ]);
  } finally {
    if (timer !== void 0) clearTimeout(timer);
  }
}
function isPromiseLike(v) {
  return v !== null && typeof v === "object" && "then" in v && typeof v.then === "function";
}
function dispatch(callSink, sinkLabel) {
  const snapshot = sinks.slice();
  if (snapshot.length === 0) return;
  const loop = (async () => {
    for (const { sink } of snapshot) {
      const outcome = await callWithTimeout(() => {
        const r = callSink(sink);
        if (r === void 0) return void 0;
        return r;
      });
      if (outcome === "timeout") {
        emitMetaWarning({
          code: "SINK_TIMEOUT",
          message: `Sink ${sinkLabel} exceeded ${SINK_TIMEOUT_MS}ms; continuing.`
        });
      } else if (typeof outcome === "object") {
        emitMetaWarning({
          code: "SINK_FAILED",
          message: `Sink ${sinkLabel} threw; continuing. ${stringifyError(outcome.error)}`,
          cause: outcome.error
        });
      }
    }
  })();
  inFlightDispatches.add(loop);
  void loop.finally(() => inFlightDispatches.delete(loop));
}
function dispatchError(serialized) {
  if (serialized.__schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
    emitMetaWarning({
      code: "SCHEMA_VERSION_MISMATCH",
      message: `SerializedError __schemaVersion=${String(
        serialized.__schemaVersion
      )} not supported; sinks may need to gracefully degrade.`
    });
  }
  dispatch((sink) => sink.onError?.(serialized), "onError");
}
function dispatchWarning(warning) {
  dispatch((sink) => sink.onWarning?.(warning), "onWarning");
}
function stringifyError(err) {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  try {
    return String(err);
  } catch {
    return "<unstringifiable>";
  }
}

// src/panic.ts
var RELATED_MAX = 32;
var POLLUTION_KEYS = /* @__PURE__ */ new Set(["__proto__", "constructor", "prototype"]);
var DEFAULT_KIND = "contract";
function panic(opts) {
  if (opts === null || typeof opts !== "object") {
    throw constructErrorByKind("security", {
      lib: "@norkron/web-errors",
      fn: "panic",
      code: "PANIC_OPTS_INVALID",
      kind: "security",
      severity: "error",
      message: "panic() called with non-object opts"
    });
  }
  const hasOwn = Object.prototype.hasOwnProperty;
  for (const key2 of POLLUTION_KEYS) {
    if (hasOwn.call(opts, key2)) {
      throw new NorkronSecurityError({
        lib: "@norkron/web-errors",
        fn: "panic",
        code: "PANIC_PROTOTYPE_POLLUTION",
        kind: "security",
        severity: "error",
        message: `panic() opts contained forbidden key: ${key2}`
      });
    }
  }
  let libId;
  try {
    if (typeof opts.lib !== "string" || opts.lib.length === 0 || opts.lib.length > 256) {
      throw new TypeError("lib must be a non-empty string \u2264256 chars");
    }
    const _libToken = opts.libToken;
    const providedToken = typeof _libToken === "symbol" ? _libToken : void 0;
    const canonical = validateLibIdentity(opts.lib, providedToken).canonicalLib;
    libId = isCodeRegistered(opts.lib, opts.code) ? opts.lib : canonical;
  } catch (cause) {
    throw new NorkronSecurityError({
      lib: "@norkron/web-errors",
      fn: "panic",
      code: "LIB_NAME_UNREGISTERED",
      kind: "security",
      severity: "error",
      message: "panic() called with invalid lib identity",
      errorCause: cause
    });
  }
  if (typeof opts.fn !== "string" || opts.fn.length === 0) {
    throw new NorkronSecurityError({
      lib: "@norkron/web-errors",
      fn: "panic",
      code: "PANIC_OPTS_INVALID",
      kind: "security",
      severity: "error",
      message: "panic() opts.fn must be a non-empty string"
    });
  }
  if (typeof opts.code !== "string" || opts.code.length === 0) {
    throw new NorkronSecurityError({
      lib: "@norkron/web-errors",
      fn: "panic",
      code: "PANIC_OPTS_INVALID",
      kind: "security",
      severity: "error",
      message: "panic() opts.code must be a non-empty string"
    });
  }
  if (typeof opts.message !== "string") {
    throw new NorkronSecurityError({
      lib: "@norkron/web-errors",
      fn: "panic",
      code: "PANIC_OPTS_INVALID",
      kind: "security",
      severity: "error",
      message: "panic() opts.message must be a string"
    });
  }
  let effectiveCode = opts.code;
  if (!isCodeRegistered(libId, opts.code)) {
    effectiveCode = "UNREGISTERED";
    try {
      recordUnmappedCode(libId, opts.code);
    } catch {
    }
  }
  let related = opts.related;
  if (related !== void 0 && related.length > RELATED_MAX) {
    related = related.slice(0, RELATED_MAX);
  }
  __lockMode();
  const internal = {
    lib: libId,
    fn: opts.fn,
    code: effectiveCode,
    message: opts.message,
    kind: opts.kind ?? DEFAULT_KIND,
    severity: "error",
    ...opts.prop !== void 0 ? { prop: opts.prop } : {},
    ...opts.received !== void 0 ? { received: opts.received } : {},
    ...opts.expected !== void 0 ? { expected: opts.expected } : {},
    ...opts.causeHint !== void 0 ? { causeHint: opts.causeHint } : {},
    ...opts.errorCause !== void 0 ? { errorCause: opts.errorCause } : {},
    ...opts.actions !== void 0 ? { actions: opts.actions } : {},
    ...opts.reproTest !== void 0 ? { reproTest: opts.reproTest } : {},
    ...related !== void 0 ? { related } : {}
  };
  const error = constructErrorByKind(internal.kind, internal);
  try {
    dispatchError(error.toJSON());
  } catch {
  }
  throw error;
}

// src/warn.ts
var _stringDedup = /* @__PURE__ */ new Map();
var _objectDedup = /* @__PURE__ */ new WeakSet();
function shouldFire(lib, fn, code, dedupKey) {
  if (dedupKey === void 0) {
    const key3 = `${lib}:${fn}:${code}`;
    if (_stringDedup.has(key3)) return false;
    _stringDedup.set(key3, true);
    return true;
  }
  if (typeof dedupKey === "object" && dedupKey !== null) {
    if (_objectDedup.has(dedupKey)) return false;
    _objectDedup.add(dedupKey);
    return true;
  }
  const key2 = `${lib}:${fn}:${code}:${String(dedupKey)}`;
  if (_stringDedup.has(key2)) return false;
  _stringDedup.set(key2, true);
  return true;
}
function warn(opts) {
  try {
    if (opts === null || typeof opts !== "object") return;
    let lib;
    let rawLib;
    try {
      const _rawLib = opts.lib;
      if (typeof _rawLib !== "string" || _rawLib.length === 0) {
        lib = "@unregistered/unknown";
      } else {
        rawLib = _rawLib;
        const _libToken = opts.libToken;
        const providedToken = typeof _libToken === "symbol" ? _libToken : void 0;
        const canonical = validateLibIdentity(_rawLib, providedToken).canonicalLib;
        lib = isCodeRegistered(_rawLib, opts.code) ? _rawLib : canonical;
      }
    } catch {
      lib = "@unregistered/unknown";
    }
    const fn = typeof opts.fn === "string" ? opts.fn : "<unknown>";
    const code = typeof opts.code === "string" && opts.code.length > 0 ? isCodeRegistered(lib, opts.code) || rawLib && isCodeRegistered(rawLib, opts.code) ? opts.code : "UNREGISTERED" : "UNREGISTERED";
    if (code === "UNREGISTERED" && typeof opts.code === "string" && opts.code.length > 0) {
      try {
        recordUnmappedCode(lib, opts.code);
      } catch {
      }
    }
    const message = typeof opts.message === "string" ? opts.message : "";
    let severity;
    if (opts.severity !== void 0) {
      severity = opts.severity;
    } else {
      const registeredSeverity = getCodeMeta(lib, code)?.severity ?? (rawLib ? getCodeMeta(rawLib, code)?.severity : void 0);
      severity = registeredSeverity ?? "warning";
    }
    if (!shouldFire(lib, fn, code, opts.dedupKey)) return;
    __lockMode();
    const mode = getNorkronErrorsMode();
    const correlationId = getCurrentCorrelationId();
    const frequency = bumpFrequency(`${lib}:${fn}:${code}`);
    const payload = {
      mode,
      lib,
      fn,
      code,
      message,
      correlationId,
      timestamp: Date.now(),
      frequency,
      severity
    };
    dispatchWarning(payload);
  } catch {
  }
}
function __resetWarnDedup() {
  _stringDedup.clear();
}

// src/deprecate.ts
function deprecate(opts) {
  const parts = [];
  if (typeof opts.message === "string" && opts.message.length > 0) {
    parts.push(opts.message);
  }
  parts.push(`Deprecated since ${opts.since}.`);
  parts.push(`Will be removed in ${opts.removeIn}.`);
  if (opts.replacement !== void 0) {
    parts.push(`Use ${opts.replacement} instead.`);
  }
  if (opts.migrationCodemod !== void 0) {
    parts.push(`Codemod: ${opts.migrationCodemod}`);
  }
  const message = parts.join(" ");
  warn({
    ...opts,
    message,
    severity: "warning"
  });
}

// src/safe.ts
function safe(fn) {
  try {
    return { ok: true, value: fn() };
  } catch (error) {
    return { ok: false, error };
  }
}
async function safeAsync(fn) {
  try {
    const value = await fn();
    return { ok: true, value };
  } catch (error) {
    return { ok: false, error };
  }
}

// src/validation.ts
function buildInvalidInputPayloads(parseResult) {
  if (!parseResult || parseResult.success !== false) return [];
  const out = [];
  const issues = parseResult.error?.issues ?? [];
  for (const issue of issues) {
    const fieldPath = (issue.path ?? []).map((p) => String(p));
    const expected = typeof issue.expected === "string" && issue.expected.length > 0 ? issue.expected : issue.code ?? issue.message ?? "valid input";
    const received = "received" in issue ? issue.received : void 0;
    out.push({ fieldPath, expected, received });
  }
  return out;
}
function invalidInputCodeFor(libName) {
  const stripped = libName.replace(/^@norkron\//, "");
  const slug = stripped.replace(/[^a-zA-Z0-9]+/g, "_").toUpperCase();
  return `${slug}_INVALID_INPUT`;
}
function reportInvalidInput(opts) {
  const payloads = buildInvalidInputPayloads(opts.parseResult);
  if (payloads.length === 0) return payloads;
  const code = opts.code ?? invalidInputCodeFor(opts.lib);
  const severity = opts.severity ?? "warning";
  for (const payload of payloads) {
    const fieldKey = payload.fieldPath.join(".") || "<root>";
    const dedupKey = `${opts.lib}:${opts.fn}:${code}:${fieldKey}`;
    const message = `${opts.fn} received invalid input at \`${fieldKey}\`: expected ${payload.expected}.`;
    warn({
      lib: opts.lib,
      ...opts.libToken !== void 0 ? { libToken: opts.libToken } : {},
      fn: opts.fn,
      code,
      message,
      severity,
      prop: fieldKey,
      expected: payload.expected,
      received: payload.received,
      dedupKey
    });
  }
  if (!isCodeRegistered(opts.lib, code)) ;
  return payloads;
}

// src/scoped.ts
function deriveDefaultToken(libName) {
  return /* @__PURE__ */ Symbol.for(`norkron.lib.${libName}`);
}
function createScopedErrors(libName, token) {
  if (typeof libName !== "string" || libName.length === 0) {
    throw new TypeError(
      "[norkron/errors] createScopedErrors: libName must be a non-empty string"
    );
  }
  const boundToken = typeof token === "symbol" ? token : deriveDefaultToken(libName);
  try {
    registerLib(libName, boundToken);
  } catch {
  }
  const scopedWarn = (opts) => warn({
    // caller overrides win if explicitly provided
    lib: libName,
    libToken: boundToken,
    ...opts
  });
  const scopedPanic = (opts) => panic({
    lib: libName,
    libToken: boundToken,
    ...opts
  });
  const scopedReportInvalidInput = (opts) => reportInvalidInput({
    lib: libName,
    libToken: boundToken,
    ...opts
  });
  return {
    warn: scopedWarn,
    panic: scopedPanic,
    reportInvalidInput: scopedReportInvalidInput,
    token: boundToken
  };
}
function createScopedWarn(libName, token) {
  return createScopedErrors(libName, token).warn;
}
function createScopedPanic(libName, token) {
  return createScopedErrors(libName, token).panic;
}

// src/dev-console-sink.ts
var installed = null;
function formatLib(lib) {
  if (!lib) return "@norkron/unknown";
  return lib.startsWith("@") ? lib : `@norkron/${lib}`;
}
function installDevConsoleSink(opts = {}) {
  if (installed) return installed;
  const out = opts.console ?? (typeof console !== "undefined" ? console : null);
  if (!out) {
    installed = () => {
    };
    return installed;
  }
  const verbose = opts.verbose !== false;
  const sink = {
    onWarning: (w) => {
      const prefix = `[${formatLib(w.lib)}:${w.code}]`;
      if (verbose) {
        out.warn(prefix, w.message, w);
      } else {
        out.warn(prefix, w.message);
      }
    },
    onError: (e) => {
      const prefix = `[${formatLib(e.lib)}:${e.code}]`;
      const propPart = e.prop !== void 0 ? ` prop=${e.prop}` : "";
      const summary = `${e.fn}: ${e.code}${propPart}`;
      if (verbose) {
        out.error(prefix, summary, e);
      } else {
        out.error(prefix, summary);
      }
    }
  };
  const off = setSink(sink);
  installed = () => {
    off();
    installed = null;
  };
  return installed;
}
function uninstallDevConsoleSink() {
  if (installed) installed();
}

// src/runtime-monitors.ts
var WEB_ERRORS_LIB = "@norkron/web-errors";
registerCodes(WEB_ERRORS_LIB, {
  ER_GPU_VALIDATION_FLOOD: {
    description: "\u22655 GPU validation messages (WebGPU swapchain / extent / texture / commandbuffer) fired inside a 2-second rolling window. The renderer is in a degraded state but each individual message is below the dev-warn threshold, so the silent-failure mode escapes the structural conformance rules.",
    severity: "critical-but-non-throwing",
    docsAnchor: "https://norkron.dev/errors/v0/errors/ER_GPU_VALIDATION_FLOOD",
    exampleFix: "Inspect the sample message; usually a stale GPUTexture/GPUTextureView outliving its swapchain, or a viewport resize that bypassed configure(). Hold the canvas in a ref and call ctx.configure() exactly once per size change."
  },
  ER_UNHANDLED_REJECTION: {
    description: "A promise rejected with no `.catch` handler attached. Triggered from `window.unhandledrejection`. Surfaces as a silent failure in dev (DevTools logs but no telemetry record).",
    severity: "critical-but-non-throwing",
    docsAnchor: "https://norkron.dev/errors/v0/errors/ER_UNHANDLED_REJECTION",
    exampleFix: "Locate the awaiting call site; either await/catch the promise or attach `.catch(err => warn({ ... }))` to give it a code."
  },
  ER_UNCAUGHT_EXCEPTION: {
    description: "A synchronous exception bubbled to `window.onerror`. Either no error boundary covered the call site, or the throw happened outside the React tree (event handler, timer, microtask).",
    severity: "critical-but-non-throwing",
    docsAnchor: "https://norkron.dev/errors/v0/errors/ER_UNCAUGHT_EXCEPTION",
    exampleFix: "Wrap the call site in `safe()` from `@norkron/web-errors`, or add a React ErrorBoundary at the route level."
  },
  ER_RESIZE_OBSERVER_LOOP: {
    description: "ResizeObserver delivered an observation that mutated layout, retriggering itself. The browser breaks the loop after one extra frame; dev-time is noisy but a real perf hazard (every frame costs a layout pass).",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/ER_RESIZE_OBSERVER_LOOP",
    exampleFix: "Schedule the dependent layout write inside `requestAnimationFrame` so the next observation runs against a stable layout, or debounce via `requestIdleCallback`."
  },
  ER_FETCH_ERROR_FLOOD: {
    description: "`fetch` returned \u2265400 across \u22653 distinct URL prefixes in a 10-second window, OR the same URL failed \u22655 times. Indicates a backend outage / auth drift / CORS regression \u2014 not a single user error.",
    severity: "warning",
    docsAnchor: "https://norkron.dev/errors/v0/errors/ER_FETCH_ERROR_FLOOD",
    exampleFix: "Inspect the sample URL + status; if 401/403 your token rotation is broken, if 5xx your origin is down, if mixed your service mesh is dropping traffic."
  }
});
var GPU_PATTERNS = [
  /swapchain/i,
  /Extent3D\s+width:/,
  /Extent3D\s+height:/,
  /Invalid\s+Texture(?:View)?\b/,
  /Invalid\s+CommandBuffer\b/,
  /Could not create/,
  /WebGPU:\s+too many warnings/
];
function gpuMessageMatches(args) {
  for (const arg of args) {
    const s = typeof arg === "string" ? arg : safeStringify(arg);
    if (!s) continue;
    for (const p of GPU_PATTERNS) {
      if (p.test(s)) return true;
    }
  }
  return false;
}
function safeStringify(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    if (v instanceof Error) return v.message;
    return String(v.message ?? "");
  } catch {
    return "";
  }
}
var EMIT_COOLDOWN_MS = 5e3;
var lastEmitAt = /* @__PURE__ */ new Map();
function rateLimited(code, now) {
  const prev = lastEmitAt.get(code) ?? 0;
  if (now - prev < EMIT_COOLDOWN_MS) return true;
  lastEmitAt.set(code, now);
  return false;
}
function makeFlood(windowMs, threshold) {
  return { windowMs, threshold, times: [], firstSample: "" };
}
function recordFlood(t, now, sample) {
  const cutoff = now - t.windowMs;
  while (t.times.length > 0 && t.times[0] < cutoff) t.times.shift();
  if (t.times.length === 0) t.firstSample = sample;
  t.times.push(now);
  return t.times.length >= t.threshold;
}
function drainFlood(t) {
  const out = { count: t.times.length, sample: t.firstSample };
  t.times.length = 0;
  t.firstSample = "";
  return out;
}
var installed2 = null;
function installRuntimeMonitors(opts = {}) {
  if (installed2) return installed2;
  if (typeof window === "undefined") {
    const noop = () => {
    };
    installed2 = noop;
    return noop;
  }
  const cleanups = [];
  const consoleTarget = opts.console ?? globalThis.console;
  const gpuOn = opts.gpu !== false;
  const rejOn = opts.unhandledRejection !== false;
  const errOn = opts.uncaughtException !== false;
  const roOn = opts.resizeObserverLoop !== false;
  const fetchOn = opts.fetchFlood !== false;
  if (gpuOn) {
    const flood = makeFlood(2e3, 5);
    const origWarn = consoleTarget.warn.bind(consoleTarget);
    const origError = consoleTarget.error.bind(consoleTarget);
    const wrap = (orig) => (...args) => {
      try {
        if (gpuMessageMatches(args)) {
          const now = Date.now();
          const sample = args.map((a) => typeof a === "string" ? a : safeStringify(a)).filter(Boolean).join(" ").slice(0, 500);
          if (recordFlood(flood, now, sample)) {
            if (!rateLimited("ER_GPU_VALIDATION_FLOOD", now)) {
              const drained = drainFlood(flood);
              warn({
                lib: WEB_ERRORS_LIB,
                fn: "installRuntimeMonitors.gpu",
                code: "ER_GPU_VALIDATION_FLOOD",
                message: `GPU validation flood: ${drained.count} messages in 2s. Sample: ${drained.sample}`
              });
            }
          }
        }
      } catch {
      }
      orig(...args);
    };
    consoleTarget.warn = wrap(origWarn);
    consoleTarget.error = wrap(origError);
    cleanups.push(() => {
      consoleTarget.warn = origWarn;
      consoleTarget.error = origError;
    });
  }
  if (rejOn) {
    const handler = (ev) => {
      try {
        const reason = ev.reason;
        const msg = reason instanceof Error ? `${reason.name}: ${reason.message}` : safeStringify(reason) || "<unknown rejection>";
        warn({
          lib: WEB_ERRORS_LIB,
          fn: "installRuntimeMonitors.unhandledrejection",
          code: "ER_UNHANDLED_REJECTION",
          message: msg.slice(0, 500),
          dedupKey: msg.slice(0, 120)
        });
      } catch {
      }
    };
    window.addEventListener("unhandledrejection", handler);
    cleanups.push(
      () => window.removeEventListener("unhandledrejection", handler)
    );
  }
  if (errOn || roOn) {
    const handler = (ev) => {
      try {
        const msg = ev.message ?? "";
        const isRoLoop = typeof msg === "string" && (msg.includes("ResizeObserver loop limit exceeded") || msg.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ));
        if (isRoLoop) {
          if (!roOn) return;
          warn({
            lib: WEB_ERRORS_LIB,
            fn: "installRuntimeMonitors.resizeObserverLoop",
            code: "ER_RESIZE_OBSERVER_LOOP",
            message: msg.slice(0, 500),
            dedupKey: "resize-observer-loop"
          });
          return;
        }
        if (!errOn) return;
        const where = ev.filename ? ` at ${ev.filename}:${ev.lineno ?? "?"}:${ev.colno ?? "?"}` : "";
        warn({
          lib: WEB_ERRORS_LIB,
          fn: "installRuntimeMonitors.error",
          code: "ER_UNCAUGHT_EXCEPTION",
          message: `${msg}${where}`.slice(0, 500),
          dedupKey: `${msg.slice(0, 80)}${where}`
        });
      } catch {
      }
    };
    window.addEventListener("error", handler);
    cleanups.push(() => window.removeEventListener("error", handler));
  }
  if (fetchOn && typeof window.fetch === "function") {
    const origFetch = window.fetch.bind(window);
    const WINDOW_MS = 1e4;
    const prefixHits = /* @__PURE__ */ new Map();
    const urlHits = /* @__PURE__ */ new Map();
    let lastSampleUrl = "";
    let lastSampleStatus = 0;
    const prune = (arr, now) => {
      const cutoff = now - WINDOW_MS;
      while (arr.length > 0 && arr[0] < cutoff) arr.shift();
    };
    const wrappedFetch = async (input, init) => {
      const res = await origFetch(input, init);
      try {
        if (!res.ok && res.status >= 400) {
          const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
          const prefix = url.split("?")[0].split("#")[0];
          const now = Date.now();
          lastSampleUrl = url;
          lastSampleStatus = res.status;
          let urlArr = urlHits.get(url);
          if (!urlArr) {
            urlArr = [];
            urlHits.set(url, urlArr);
          }
          prune(urlArr, now);
          urlArr.push(now);
          let prefArr = prefixHits.get(prefix);
          if (!prefArr) {
            prefArr = [];
            prefixHits.set(prefix, prefArr);
          }
          prune(prefArr, now);
          prefArr.push(now);
          let distinctPrefixes = 0;
          for (const arr of prefixHits.values()) {
            prune(arr, now);
            if (arr.length > 0) distinctPrefixes++;
          }
          const sameUrlFloods = urlArr.length >= 5;
          const distinctFloods = distinctPrefixes >= 3;
          if ((sameUrlFloods || distinctFloods) && !rateLimited("ER_FETCH_ERROR_FLOOD", now)) {
            warn({
              lib: WEB_ERRORS_LIB,
              fn: "installRuntimeMonitors.fetchFlood",
              code: "ER_FETCH_ERROR_FLOOD",
              message: `fetch error flood: ${sameUrlFloods ? `${urlArr.length} hits on ${url}` : `${distinctPrefixes} distinct URL prefixes`} in 10s. Last: ${lastSampleStatus} ${lastSampleUrl}`
            });
          }
        }
      } catch {
      }
      return res;
    };
    const installedFetch = Object.assign(
      wrappedFetch,
      origFetch
    );
    window.fetch = installedFetch;
    cleanups.push(() => {
      window.fetch = origFetch;
    });
  }
  const uninstall = () => {
    for (const c of cleanups.splice(0)) {
      try {
        c();
      } catch {
      }
    }
    installed2 = null;
  };
  installed2 = uninstall;
  return uninstall;
}

// src/sourcemap-probe.ts
var leakSink;
var probed = false;
function setSourcemapLeakSink(sink) {
  leakSink = sink;
}
async function probeSourcemapLeak(opts = {}) {
  if (typeof process !== "undefined" && process.env && process.env.NORKRON_ERRORS_NO_SOURCEMAP_PROBE === "1") {
    return false;
  }
  if (probed) return false;
  probed = true;
  const mode = opts.mode ?? readMode2();
  if (mode !== "production" && opts.suppressInDev !== false) {
    return false;
  }
  const url = opts.url ?? detectBundleUrl();
  if (!url) return false;
  const fetchImpl = opts.fetchImpl ?? (typeof fetch === "function" ? fetch : void 0);
  if (!fetchImpl) return false;
  const candidates = buildMapCandidates(url);
  for (const candidate of candidates) {
    try {
      const res = await fetchImpl(candidate, {
        method: "HEAD",
        credentials: "omit",
        cache: "no-store"
      });
      if (res && typeof res.status === "number" && res.status === 200) {
        fireLeak("@norkron/errors:sourcemap-leak");
        return true;
      }
    } catch (e) {
      const msg = e instanceof Error && e.message || String(e);
      if (/CSP|Content Security|blocked|CORS/i.test(msg)) {
        fireLeak("@norkron/errors:sourcemap-leak:csp-blocked");
        return false;
      }
    }
  }
  return false;
}
function fireLeak(lib) {
  if (leakSink) {
    try {
      leakSink(lib);
    } catch {
    }
  }
}
function buildMapCandidates(url) {
  const out = [];
  const cleaned = url.replace(/[?#].*$/, "");
  out.push(cleaned + ".map");
  if (/\.[mc]?js$/i.test(cleaned)) {
    out.push(cleaned.replace(/\.[mc]?js$/i, ".js.map"));
  }
  return [...new Set(out)];
}
function detectBundleUrl() {
  try {
    const metaUrl = import.meta?.url;
    if (typeof metaUrl === "string") return metaUrl;
  } catch {
  }
  try {
    const stack = new Error().stack;
    if (typeof stack === "string") {
      const m = /(https?:\/\/[^\s)]+\.[mc]?js)/i.exec(stack);
      if (m) return m[1];
      const f = /(file:\/\/\/[^\s)]+\.[mc]?js)/i.exec(stack);
      if (f) return f[1];
    }
  } catch {
  }
  return void 0;
}
function readMode2() {
  const g = globalThis;
  const sentinel = g.__NORKRON_ERRORS_MODE__;
  if (sentinel === "production" || sentinel === "development" || sentinel === "test") return sentinel;
  if (typeof process !== "undefined" && process.env) {
    const m = process.env.NORKRON_ERRORS_MODE;
    if (m === "production" || m === "development" || m === "test") return m;
    if (process.env.NODE_ENV === "production") return "production";
    if (process.env.NODE_ENV === "test") return "test";
  }
  return "development";
}

export { ERROR_CODES, LIB_VERSION, MAX_SINKS, NorkronContractError, NorkronEnvironmentError, NorkronError, NorkronLockedModeError, NorkronRangeError, NorkronSecurityError, NorkronStateError, NorkronSyntaxError, NorkronTypeError, NorkronVersionError, SINK_TIMEOUT_MS, SUPPORTED_SCHEMA_VERSION, __captureModeSnapshot, __resetNorkronErrorsMode, __resetWarnDedup, adoptCorrelation, applyDevOnlyDescriptors, applyInspectHook, applyToPrimitiveHook, assertCodeRegistered, buildInvalidInputPayloads, bumpFrequency, captureSourceLocation, clearFrequencyCache, clearSinks, configureSourceMap, createScopedErrors, createScopedPanic, createScopedWarn, deprecate, detectPii, firstPiiKind, getCodeMeta, getCorrelationSeed, getCurrentCorrelationId, getFrequencySnapshot, getLibToken, getNorkronErrorsMode, getRedactedStackString, getUnmappedCodes, installDevConsoleSink, installRuntimeMonitors, invalidInputCodeFor, isCodeRegistered, isNorkronError, isNorkronWarning, listCodes, listLibs, panic, probeSourcemapLeak, rafWithCorrelation, recordUnmappedCode, redactExpected, redactReceived, redactStack, redactString, registerCodes, registerLib, reportInvalidInput, resetUnmappedCodes, safe, safeAsync, scheduleWithCorrelation, serializeCorrelation, setCorrelationProvider, setCorrelationSeed, setMetaWarningHandler, setNorkronErrorsMode, setSink, setSourcemapLeakSink, uninstallDevConsoleSink, useCorrelationBoundary, validateCallerStackFrame, validateLibIdentity, warn, withCorrelation, withCorrelationAsync };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map